/**
 * FSA proration — split an annual FSA election across the EXACT pay periods that
 * remain in the plan year, with a last-paycheck "true-up" so the deductions sum
 * back to the election to the cent.
 *
 * Pure (no DB/React); shared by the enrollment routes (to persist
 * enrollments.payrollDeduction) and the enrollment UI (live preview).
 *
 * Exactness depends on the pay schedule:
 *  - semi-monthly / monthly: exact without an anchor (fixed calendar days).
 *  - weekly / bi-weekly WITH an anchor: exact (real paydays stepped from anchor).
 *  - weekly / bi-weekly WITHOUT an anchor: estimated by calendar math (we can't
 *    align to real paydays), flagged `isExact: false`.
 */

import { type PaySchedule, countRemainingPayPeriods } from "./payrollSchedule";

const MS_PER_DAY = 86_400_000;

export interface FsaProrationInput {
  /** The employee's annual FSA election (dollars). */
  annualElection: number;
  /** Pay schedule (frequency + anchor) for the employee's pay group. */
  schedule: PaySchedule;
  /** Enrollment effective date (start of the deduction window). */
  from: Date;
  /** Plan-year end (last day deductions can be taken). */
  to: Date;
}

export interface FsaProrationResult {
  /** Pay periods left in [from, to]. */
  remainingPeriods: number;
  /** Steady per-paycheck deduction (rounded down to the cent). */
  perPaycheck: number;
  /** Final paycheck deduction — absorbs the rounding remainder (the true-up). */
  lastPaycheck: number;
  /** Extra cents the final paycheck carries over the steady amount. */
  trueUp: number;
  /** False when the count was estimated (weekly/bi-weekly with no anchor). */
  isExact: boolean;
}

/**
 * True when the schedule can produce an EXACT FSA deduction: weekly/bi-weekly
 * need an anchor pay date; semi-monthly/monthly are always exact. FSA enrollment
 * requires this (no estimate) — the enrollment guard rejects unready schedules.
 */
export function isPayScheduleAnchored(schedule: PaySchedule | null | undefined): boolean {
  if (!schedule) return false;
  const needsAnchor = schedule.payFrequency === "weekly" || schedule.payFrequency === "biweekly";
  return !needsAnchor || !!schedule.anchorDate;
}

/** Estimate remaining weekly/bi-weekly paychecks when there's no anchor to align to. */
function estimateRemainingPeriods(schedule: PaySchedule, from: Date, to: Date): number {
  const interval = schedule.payFrequency === "weekly" ? 7 : 14;
  const days = Math.floor((to.getTime() - from.getTime()) / MS_PER_DAY) + 1; // inclusive
  if (days <= 0) return 0;
  return Math.ceil(days / interval);
}

export function computeFsaProration(input: FsaProrationInput): FsaProrationResult {
  const { annualElection, schedule, from, to } = input;

  const needsAnchor = schedule.payFrequency === "weekly" || schedule.payFrequency === "biweekly";
  let isExact = true;
  let remainingPeriods = countRemainingPayPeriods(schedule, from, to);
  if (needsAnchor && !schedule.anchorDate) {
    remainingPeriods = estimateRemainingPeriods(schedule, from, to);
    isExact = false;
  }

  if (remainingPeriods <= 0 || !(annualElection > 0)) {
    return {
      remainingPeriods: Math.max(remainingPeriods, 0),
      perPaycheck: 0,
      lastPaycheck: 0,
      trueUp: 0,
      isExact,
    };
  }

  // Work in integer cents so the periods sum back to the election exactly.
  const totalCents = Math.round(annualElection * 100);
  const perCents = Math.floor(totalCents / remainingPeriods);
  const lastCents = totalCents - perCents * (remainingPeriods - 1);

  return {
    remainingPeriods,
    perPaycheck: perCents / 100,
    lastPaycheck: lastCents / 100,
    trueUp: (lastCents - perCents) / 100,
    isExact,
  };
}
